import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from 'moment';
import { propertyOperators } from '@appcues/libcues';
import { millisecondsPerDay } from 'constants/account/conditions';
import { Select } from 'next/components/ui';
import ToggleableSelect from 'components/Common/Clauses/ToggleableSelect';
import ToggleableInput from 'components/Common/Clauses/ToggleableInput';

const OPTIONS = [
  { value: 'ever', label: 'previously' },
  { value: propertyOperators.SINCE, label: 'since' },
  {
    value: propertyOperators.LESS_THAN_X_DAYS_AGO,
    label: 'in the last',
  },
];

const RELATIVE_TIME_LABEL = {
  ever: 'previously',
  [propertyOperators.SINCE]: 'since',
  [propertyOperators.LESS_THAN_X_DAYS_AGO]: 'in the last',
};

const Wrapper = styled.div`
  align-items: center;
  display: flex;
  padding: 10px 0;

  > * {
    margin-right: 10px;
    max-width: 200px;
  }

  ${Select} {
    min-width: 200px;
  }
`;

export default function UserHistoryDateRange({
  clause,
  readOnly,
  updateClause,
}) {
  const operator = clause.operator || 'ever';
  const times = clause.times == null ? 1 : clause.times;

  const value =
    operator === 'since' && clause.value === '0' ? 'ever' : operator;

  const datePickerDisplayTime = moment(
    Number.parseInt(clause.value, 10)
  ).format('L');

  const selected = OPTIONS.find(option =>
    operator === 'since' && clause.value === '0'
      ? option.value === 'ever'
      : option.value === operator
  );

  return (
    <Wrapper>
      <span>At least</span>

      <ToggleableInput
        type="text"
        value={times}
        onChange={({ target }) =>
          updateClause(clause.id, {
            times:
              target.value && Number.parseInt(target.value, 10)
                ? Number.parseInt(target.value || 0, 10)
                : '',
          })
        }
        readOnly={readOnly}
        readOnlyContent={times}
      />

      <span>{times === 1 ? 'time' : 'times'}</span>

      <ToggleableSelect
        readOnly={readOnly}
        readOnlyContent={RELATIVE_TIME_LABEL[value]}
        value={selected}
        options={OPTIONS}
        onChange={opt => {
          switch (opt.value) {
            case 'ever':
              updateClause(clause.id, {
                operator: propertyOperators.SINCE,
                value: '0',
              });
              break;

            case propertyOperators.SINCE:
              updateClause(clause.id, {
                operator: propertyOperators.SINCE,
                value: moment().startOf('day').format('x'),
              });
              break;

            case propertyOperators.LESS_THAN_X_DAYS_AGO:
              updateClause(clause.id, {
                operator: propertyOperators.LESS_THAN_X_DAYS_AGO,
                value: millisecondsPerDay,
              });
              break;

            default:
              break;
          }
        }}
        operator
      />
      {(() => {
        // FIXME: PLEASE remove this IIFE pattern...
        switch (operator) {
          case propertyOperators.SINCE:
            if (clause.value && clause.value !== '0') {
              return (
                <ToggleableInput
                  datePicker
                  selected={moment(Number.parseInt(clause.value, 10))}
                  onChange={date =>
                    updateClause(clause.id, {
                      value: date.startOf('day').format('x'),
                    })
                  }
                  readOnly={readOnly}
                  readOnlyContent={datePickerDisplayTime}
                />
              );
            }
            break;
          case propertyOperators.LESS_THAN_X_DAYS_AGO: {
            const numTrailingDays = clause.value
              ? clause.value / millisecondsPerDay
              : '';
            const dayDisplay = numTrailingDays === 1 ? ' day' : ' days';
            return (
              <div>
                <ToggleableInput
                  type="text"
                  value={numTrailingDays}
                  onChange={e => {
                    const numDays = Number.parseInt(e.target.value, 10);
                    if (e.target.value === '' || numDays) {
                      updateClause(clause.id, {
                        value: numDays ? numDays * millisecondsPerDay : '',
                      });
                    }
                  }}
                  readOnly={readOnly}
                  readOnlyContent={numTrailingDays}
                />
                {dayDisplay}
              </div>
            );
          }
          default:
            break;
        }

        return null;
      })()}
    </Wrapper>
  );
}

UserHistoryDateRange.propTypes = {
  clause: PropTypes.shape({
    id: PropTypes.string,
    operator: PropTypes.string,
    times: PropTypes.number,
    value: PropTypes.string,
  }),
  readOnly: PropTypes.bool,
  updateClause: PropTypes.func,
};
